import { apiErrorMessage } from "@/utils/apiError-helper";

/** Axios's own text when the body carried nothing — names a status code and nothing else. */
const AXIOS_STATUS_TEXT = /^Request failed with status code \d+$/;

/**
 * The sentence to show when a write fails, chosen by STATUS rather than blanket-replaced.
 *
 * 401/403 bodies ("Forbidden: target out of scope") and 5xx / no-response bodies are written for an
 * engineer and must never reach a customer; every other 4xx is a refusal a controller wrote for a
 * human ("Budget is locked once a campaign is scheduled."), and that sentence is the one worth reading.
 */
export function saveErrorMessage(err: unknown, fallback: string): string {
  const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
  // 402 is excluded too: its body names an internal constraint key ("wallet.multi_group") and is
  // rendered as upsell copy by `refusalMessage`, never shown raw.
  const isBusinessRefusal =
    typeof status === "number" &&
    status >= 400 &&
    status < 500 &&
    status !== 401 &&
    status !== 402 &&
    status !== 403;
  if (!isBusinessRefusal) return fallback;

  const message = apiErrorMessage(err, fallback);
  return AXIOS_STATUS_TEXT.test(message) ? fallback : message;
}

/**
 * The sentence to show when a write fails, distinguishing an ACCESS denial from any other failure.
 *
 * A 401/403 is the server refusing on authorization grounds. Its body ("Forbidden: target out of
 * scope") is written for an engineer and must never be shown (same rule as `saveErrorMessage`), but
 * the *fact* of the denial is worth stating plainly — "You don't have access to …" tells the user
 * what happened, where "Please try again" would send them in a loop a retry can't break.
 *
 * Everything else defers to `saveErrorMessage`: a genuine business refusal (e.g. "Budget is locked
 * once a campaign is scheduled.") keeps the server's own sentence; a 5xx or an offline request
 * falls to `fallback`.
 */
export function writeDenialMessage(
  err: unknown,
  { denied, fallback }: { denied: string; fallback: string },
): string {
  const status = (err as { response?: { status?: number } } | undefined)?.response?.status;
  if (status === 401 || status === 403) return denied;
  return saveErrorMessage(err, fallback);
}
